import { ImportStepper } from '@ooh/ui/components/import-stepper';
import type { ImportPhase } from './import-phase';

// Standard 4-step flow (upload → validation → progress → result).
// When the uploaded file is non-standard, a 5th "mapping" step is inserted between upload and
// validation. The stepper derives its step list from whichever flow is active so it never shows
// a skipped step in the standard path.
const STANDARD_PHASE_ORDER: ImportPhase[] = ['upload', 'validation', 'progress', 'result'];
const STANDARD_DEDUP_PHASE_ORDER: ImportPhase[] = [
  'upload',
  'supplier-dedup',
  'validation',
  'progress',
  'result',
];
const MAPPING_PHASE_ORDER: ImportPhase[] = [
  'upload',
  'mapping',
  'validation',
  'progress',
  'result',
];
// The ZIP photo-bundle flow jumps from upload straight to progress; it never validates.
const PHOTO_BUNDLE_PHASE_ORDER: ImportPhase[] = ['upload', 'progress', 'result'];

interface StepperStepLabels {
  upload: string;
  supplierDedup: string;
  mapping: string;
  validation: string;
  progress: string;
  result: string;
}

/**
 * Derives the stepper step list + phase order for the active flow. Exported for unit testing.
 *
 * - photo-bundle (ZIP): upload → progress → result (no validation, no mapping)
 * - XLSX non-standard: upload → mapping → validation → progress → result
 * - XLSX standard with dedup: upload → supplier-dedup → validation → progress → result
 * - XLSX standard: upload → validation → progress → result
 */
export function deriveStepperSteps(
  currentPhase: ImportPhase,
  isPhotoBundle: boolean,
  labels: StepperStepLabels,
  isDedupActive = false,
): { steps: string[]; phaseOrder: ImportPhase[] } {
  if (isPhotoBundle) {
    return {
      steps: [labels.upload, labels.progress, labels.result],
      phaseOrder: PHOTO_BUNDLE_PHASE_ORDER,
    };
  }
  const isNonStandardFlow = currentPhase === 'mapping';
  if (isNonStandardFlow) {
    return {
      steps: [labels.upload, labels.mapping, labels.validation, labels.progress, labels.result],
      phaseOrder: MAPPING_PHASE_ORDER,
    };
  }
  if (isDedupActive) {
    return {
      steps: [
        labels.upload,
        labels.supplierDedup,
        labels.validation,
        labels.progress,
        labels.result,
      ],
      phaseOrder: STANDARD_DEDUP_PHASE_ORDER,
    };
  }
  return {
    steps: [labels.upload, labels.validation, labels.progress, labels.result],
    phaseOrder: STANDARD_PHASE_ORDER,
  };
}

interface WizardStepperProps {
  currentPhase: ImportPhase;
  isPhotoBundle: boolean;
  isDedupActive: boolean;
  labels: StepperStepLabels & { ariaLabel: string };
}

export function WizardStepper({
  currentPhase,
  isPhotoBundle,
  isDedupActive,
  labels,
}: WizardStepperProps) {
  const { steps, phaseOrder } = deriveStepperSteps(
    currentPhase,
    isPhotoBundle,
    labels,
    isDedupActive,
  );
  return (
    <ImportStepper
      steps={steps}
      currentStep={phaseOrder.indexOf(currentPhase)}
      ariaLabel={labels.ariaLabel}
    />
  );
}
